import { useEffect, useMemo } from 'react';
import { Cookies } from 'react-cookie';
import { useRouter } from 'next/router';

import { PdpCatalogFulfillmentType } from '@domains/pdp-common';

import { CouponRegistrationType } from '../graphqls/coupon-policy-list_types.2';
import { useCouponDownloadMutation } from '../mutations/useCouponDownloadMutation';
import { useCouponPolicyListQuery } from '../queries/useCouponPolicyListQuery';

export interface CouponAutoDownloadProps {
  shop_id: string;
  product_id: string;
  fulfillment_type: PdpCatalogFulfillmentType;
  is_logged_in: boolean;
}

export function useCouponAutoDownloadFromNaverEp({
  shop_id,
  product_id,
  fulfillment_type,
  is_logged_in,
}: CouponAutoDownloadProps) {
  const { data: coupon_policy_list_response } = useCouponPolicyListQuery({
    shop_id,
    product_id,
    include_zonly: fulfillment_type === PdpCatalogFulfillmentType.ZIGZIN,
    options: {
      enabled: is_logged_in && Boolean(shop_id) && Boolean(product_id),
    },
  });

  const { mutate: issueUserAccountShopCouponList } = useCouponDownloadMutation();

  const { query } = useRouter();
  const cookies = new Cookies();

  const utm_source = query.utm_source?.toString() || cookies.get('utm_source');

  const coupon_policy_id_list = useMemo(() => {
    return (coupon_policy_list_response?.data.coupon_policy_list.item_list ?? [])
      .filter((coupon_policy) => coupon_policy.registration_type !== CouponRegistrationType.BOOKMARK)
      .map((coupon_policy) => coupon_policy.id);
  }, [coupon_policy_list_response]);

  useEffect(() => {
    const is_naver_ep = utm_source?.includes('shopping_naver_all') || false;

    if (!is_naver_ep) {
      return;
    }

    if (!shop_id) {
      return;
    }

    if (!product_id) {
      return;
    }

    if (!coupon_policy_id_list.length) {
      return;
    }

    issueUserAccountShopCouponList({
      shop_id_list: [shop_id],
      product_id_list: [product_id],
      user_account_coupon_policy_id_list: coupon_policy_id_list,
      only_issuable: true,
    });
  }, [utm_source, shop_id, product_id, coupon_policy_id_list, issueUserAccountShopCouponList]);
}
